<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToastStore } from '../stores/toast'
import { useI18n } from 'vue-i18n'
import clubService from '../services/club.service'

const route = useRoute()
const router = useRouter()
const toastStore = useToastStore()
const { t } = useI18n()

// Get club UUID and bank account UUID from route
const clubUuid = computed(() => route.params.uuid as string)
const bankAccountUuid = computed(() => route.params.accountUuid as string)

// Form fields
const accountName = ref('')
const bankName = ref('')
const accountNumber = ref('')
const swift = ref('')
const currency = ref('PLN')

const loading = ref(false)
const loadingData = ref(false)
const error = ref<string | null>(null)
const validationErrors = ref<Record<string, string[]>>({})

// Fetch bank account data
const fetchBankAccount = async () => {
  if (!clubUuid.value) {
    error.value = t('bankAccounts.errorNoClubUuid')
    return
  }

  loadingData.value = true
  error.value = null

  try {
    const response = await clubService.getBankAccounts(clubUuid.value)
    const accounts = response.bank_accounts || []

    // Find the specific account
    const account = accounts.find((acc: any) => acc.uuid === bankAccountUuid.value)

    if (!account) {
      error.value = t('bankAccounts.bankAccountNotFound')
      return
    }

    // Populate form fields
    accountName.value = account.account_name || ''
    bankName.value = account.bank_name || ''
    accountNumber.value = formatAccountNumber(account.account_number || '')
    swift.value = account.swift || ''
    currency.value = account.currency || 'PLN'
  } catch (err: any) {
    console.error('Fetch bank account error:', err)
    error.value = err.response?.data?.message || t('bankAccounts.errorFetchingBankAccount')
  } finally {
    loadingData.value = false
  }
}

// Format account number with spaces
const formatAccountNumber = (accountNumber: string): string => {
  if (!accountNumber) return ''
  const cleaned = accountNumber.replace(/\s/g, '')
  return cleaned.replace(/(\d{2})(\d{4})(\d{4})(\d{4})(\d{4})(\d{4})(\d{4})/, '$1 $2 $3 $4 $5 $6 $7')
}

// Handle form submission
const handleSubmit = async () => {
  if (!clubUuid.value || !bankAccountUuid.value) {
    error.value = t('bankAccounts.errorNoClubOrAccountUuid')
    return
  }

  loading.value = true
  error.value = null
  validationErrors.value = {}

  try {
    const data = {
      account_name: accountName.value.trim(),
      bank_name: bankName.value.trim(),
      account_number: accountNumber.value.trim(),
      swift: swift.value.trim() || null,
      currency: currency.value
    }

    await clubService.updateBankAccount(clubUuid.value, bankAccountUuid.value, data)

    toastStore.success(t('bankAccounts.bankAccountUpdated'))
    router.push(`/club/${clubUuid.value}/bank-accounts`)
  } catch (err: any) {
    console.error('Update bank account error:', err)

    // Handle validation errors
    if (err.response?.data?.errors) {
      validationErrors.value = err.response.data.errors
    } else {
      error.value = err.response?.data?.message || t('bankAccounts.errorUpdatingBankAccount')
    }
  } finally {
    loading.value = false
  }
}

// Get validation error for field
const getFieldError = (fieldName: string): string | null => {
  const errors = validationErrors.value[fieldName]
  return errors && errors.length > 0 ? errors[0] : null
}

// Check if field has error
const hasFieldError = (fieldName: string): boolean => {
  return !!validationErrors.value[fieldName]
}

// Handle cancel
const handleCancel = () => {
  router.push(`/club/${clubUuid.value}/bank-accounts`)
}

// Fetch data on mount
onMounted(() => {
  fetchBankAccount()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="max-w-3xl mx-auto px-4 py-4">
      <!-- Back Button -->
      <div class="mb-4">
        <button
          @click="handleCancel"
          class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('bankAccounts.backToList') }}
        </button>
      </div>

      <!-- Page Header -->
      <div class="mb-4">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('bankAccounts.editBankAccount') }}</h1>
        <p class="text-sm text-gray-600 mt-1">{{ t('bankAccounts.editBankAccountSubtitle') }}</p>
      </div>

      <!-- Loading State -->
      <div v-if="loadingData" class="bg-white border-2 border-gray-300 rounded shadow-sm p-8 text-center">
        <p class="text-gray-600">{{ t('bankAccounts.loadingData') }}</p>
      </div>

      <!-- Error Message -->
      <div v-else-if="error" class="mb-4 bg-red-50 border-2 border-red-300 rounded p-3">
        <p class="text-sm text-red-800">{{ error }}</p>
      </div>

      <!-- Edit Form -->
      <form v-else @submit.prevent="handleSubmit" class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
        <!-- Nazwa konta -->
        <div class="mb-3">
          <label for="account_name" class="block text-sm font-medium text-gray-700 mb-1">
            {{ t('bankAccounts.accountName') }} <span class="text-red-600">{{ t('common.required') }}</span>
          </label>
          <input
            v-model="accountName"
            type="text"
            id="account_name"
            required
            :class="[
              'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600',
              hasFieldError('account_name') ? 'border-red-500' : 'border-gray-300'
            ]"
            :placeholder="t('bankAccounts.accountNamePlaceholder')"
          >
          <p v-if="getFieldError('account_name')" class="text-xs text-red-600 mt-1">
            {{ getFieldError('account_name') }}
          </p>
        </div>

        <!-- Nazwa banku -->
        <div class="mb-3">
          <label for="bank_name" class="block text-sm font-medium text-gray-700 mb-1">
            {{ t('bankAccounts.bankName') }} <span class="text-red-600">{{ t('common.required') }}</span>
          </label>
          <input
            v-model="bankName"
            type="text"
            id="bank_name"
            required
            :class="[
              'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600',
              hasFieldError('bank_name') ? 'border-red-500' : 'border-gray-300'
            ]"
            :placeholder="t('bankAccounts.bankNamePlaceholder')"
          >
          <p v-if="getFieldError('bank_name')" class="text-xs text-red-600 mt-1">
            {{ getFieldError('bank_name') }}
          </p>
        </div>

        <!-- Numer konta -->
        <div class="mb-3">
          <label for="account_number" class="block text-sm font-medium text-gray-700 mb-1">
            {{ t('bankAccounts.accountNumber') }} <span class="text-red-600">{{ t('common.required') }}</span>
          </label>
          <input
            v-model="accountNumber"
            type="text"
            id="account_number"
            required
            maxlength="34"
            :class="[
              'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600 font-mono',
              hasFieldError('account_number') ? 'border-red-500' : 'border-gray-300'
            ]"
            :placeholder="t('bankAccounts.accountNumberPlaceholder')"
          >
          <p class="text-xs text-gray-500 mt-1">{{ t('bankAccounts.accountNumberHint') }}</p>
          <p v-if="getFieldError('account_number')" class="text-xs text-red-600 mt-1">
            {{ getFieldError('account_number') }}
          </p>
        </div>

        <!-- SWIFT -->
        <div class="mb-3">
          <label for="swift" class="block text-sm font-medium text-gray-700 mb-1">
            SWIFT/BIC
          </label>
          <input
            v-model="swift"
            type="text"
            id="swift"
            maxlength="15"
            :class="[
              'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600 font-mono uppercase',
              hasFieldError('swift') ? 'border-red-500' : 'border-gray-300'
            ]"
            :placeholder="t('bankAccounts.swiftPlaceholder')"
          >
          <p class="text-xs text-gray-500 mt-1">{{ t('bankAccounts.swiftHint') }}</p>
          <p v-if="getFieldError('swift')" class="text-xs text-red-600 mt-1">
            {{ getFieldError('swift') }}
          </p>
        </div>

        <!-- Currency -->
        <div class="mb-3">
          <label for="currency" class="block text-sm font-medium text-gray-700 mb-1">
            {{ t('bankAccounts.currency') }} <span class="text-red-600">{{ t('common.required') }}</span>
          </label>
          <select
            v-model="currency"
            id="currency"
            required
            :class="[
              'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600',
              hasFieldError('currency') ? 'border-red-500' : 'border-gray-300'
            ]"
          >
            <option v-for="code in ['PLN', 'EUR', 'GBP', 'USD', 'CHF', 'CZK', 'DKK', 'NOK', 'SEK', 'RON', 'HRK', 'LTL']" :key="code" :value="code">{{ code }} - {{ t(`currencies.${code}`) }}</option>
          </select>
          <p v-if="getFieldError('currency')" class="text-xs text-red-600 mt-1">
            {{ getFieldError('currency') }}
          </p>
        </div>

        <!-- Submit Section -->
        <div class="pt-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            @click="handleCancel"
            class="py-2 px-4 bg-gray-100 text-gray-700 font-medium rounded hover:bg-gray-200 border border-gray-300"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            type="submit"
            :disabled="loading"
            class="py-2 px-4 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ loading ? t('bankAccounts.saving') : t('categories.saveChanges') }}
          </button>
        </div>
      </form>
    </div>
  </main>
</template>
